import { ref, set, get, onValue, off, update, runTransaction } from 'firebase/database';
import { db } from '../services/firebase';
import { Card, Player } from '../types';
import { createDeck, shuffle, calculateRoundScores, isScopaMove } from '../lib/gameUtils';
import { GAME_CONSTANTS } from '../lib/constants';

export interface MatchPlayer {
  uid: string; // '' if empty seat
  name: string; // '' if empty seat
  hand: Card[];
  captured: Card[];
  totalScore: number;
  hasDeclaredLowHand?: boolean;
  hasDeclaredThreeAces?: boolean;
  lowHandCount?: number;
  threeAcesCount?: number;
  readyForNextRound?: boolean;
  disconnected?: boolean; // true when player has disconnected (tab close, etc.)
}

export interface MatchData {
  matchId: string;
  hostUid: string;
  numPlayers: number;
  status: 'waiting' | 'playing' | 'roundEnd' | 'finished';
  deck: Card[];
  table: Card[];
  players: MatchPlayer[];
  currentPlayerIndex: number;
  roundStartPlayer: number;
  lastPlayedCard: { card: Card; playerIndex: number } | null;
  lastToCapture: number | null;
  messages: string[];
  scope: number[];
  revealedHands: string[][];
  targetScore?: number;
  createdAt?: number;
  roundEndedAt?: number;
  lastEmote?: {
    playerIndex: number;
    emote: 'angry' | 'cool' | 'shocked' | 'handshake' | 'lol';
    timestamp: number;
  } | null;
  lastMessage?: {
    playerIndex: number;
    text: string;
    timestamp: number;
  } | null;
}

/** Helper to sanitize objects for Firebase transactions (replacing undefined with null recursively) */
function sanitizeForFirebase(obj: any): any {
  if (obj === null || obj === undefined) return null;
  if (Array.isArray(obj)) {
    return obj.map(sanitizeForFirebase);
  }
  if (typeof obj === 'object') {
    const res: any = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const val = obj[key];
        if (val === undefined) {
          res[key] = null;
        } else {
          res[key] = sanitizeForFirebase(val);
        }
      }
    }
    return res;
  }
  return obj;
}

/** Generate a random 6-digit match code */
function generateMatchCode(): string {
  return Math.floor(100000 + Math.random() * 900000).toString();
}

/** Atomically reserve a unique match code using a Firebase transaction. */
async function reserveMatchCode(hostUid: string): Promise<string> {
  for (let attempt = 0; attempt < 5; attempt++) {
    const code = generateMatchCode();
    const matchRef = ref(db, `matches/${code}`);
    const result = await new Promise<{ committed: boolean; snapshot: any }>((resolve, reject) => {
      runTransaction(matchRef, (data: any) => {
        if (data === null) {
          return { _reserved: true, hostUid, createdAt: Date.now() };
        }
        return; // Abort, occupied
      }).then(resolve).catch(reject);
    });

    if (result.committed) {
      return code;
    }
  }
  throw new Error('[matchmaking] Failed to generate unique match code after 5 attempts');
}

/** Create a new match as host */
export async function createMatch(hostUid: string, hostName: string, numPlayers: number = 2, targetScore: number = 16): Promise<string> {
  try {
    const code = await reserveMatchCode(hostUid);
    const matchRef = ref(db, `matches/${code}`);

    const deck = shuffle(createDeck());
    const table = deck.splice(0, 4);

    const players: MatchPlayer[] = [];
    for (let i = 0; i < numPlayers; i++) {
      players.push({
        uid: i === 0 ? hostUid : '',
        name: i === 0 ? hostName : '',
        hand: [],
        captured: [],
        totalScore: 0,
        hasDeclaredLowHand: false,
        hasDeclaredThreeAces: false,
        lowHandCount: 0,
        threeAcesCount: 0,
      });
    }

    // For 2 players, deal cards immediately to get started on join.
    // For 3 & 4 players, deal will happen when the host starts the match.
    if (numPlayers === 2) {
      players[0].hand = deck.splice(0, 3);
      players[1].hand = deck.splice(0, 3);
    }

    const matchData: MatchData = {
      matchId: code,
      hostUid,
      numPlayers,
      status: 'waiting',
      deck,
      table,
      players,
      targetScore,
      currentPlayerIndex: 0,
      roundStartPlayer: 0,
      lastPlayedCard: null,
      lastToCapture: null,
      messages: ['In attesa di altri giocatori...'],
      scope: Array(numPlayers).fill(0),
      revealedHands: Array(numPlayers).fill([]).map(() => []),
      createdAt: Date.now(),
    };

    await set(matchRef, matchData);
    trackMyMatch(code);
    return code;
  } catch (err) {
    console.error('[matchmaking] createMatch failed:', err);
    throw new Error(`[matchmaking] Impossibile creare la partita: ${err instanceof Error ? err.message : err}`);
  }
}

export interface JoinResult {
  success: boolean;
  reason?: 'not_found' | 'full' | 'already_member' | 'wrong_status';
}

/** Join an existing match (for first-time joining — lobby only) */
export async function joinMatch(code: string, guestUid: string, guestName: string): Promise<JoinResult> {
  const matchRef = ref(db, `matches/${code}`);

  try {
    let unsubscribe: (() => void) | null = null;
    const cacheData = await new Promise<MatchData | null>((resolve) => {
      unsubscribe = onValue(matchRef, (snapshot) => {
        if (snapshot.exists()) {
          resolve(snapshot.val() as MatchData);
        } else {
          resolve(null);
        }
      }, (error) => {
        console.error('[matchmaking] Cache subscription error:', error);
        resolve(null);
      });
    });

    if (!cacheData) {
      if (unsubscribe) unsubscribe();
      return { success: false, reason: 'not_found' };
    }

    // Check if this uid is already in the match (disconnected or not)
    const existingSeat = cacheData.players.findIndex(p => p.uid === guestUid);
    if (existingSeat !== -1) {
      if (unsubscribe) unsubscribe();
      return { success: false, reason: 'already_member' };
    }

    if (cacheData.status !== 'waiting') {
      if (unsubscribe) unsubscribe();
      return { success: false, reason: 'wrong_status' };
    }

    const emptySeatIndex = cacheData.players.findIndex(p => p.uid === '');
    if (emptySeatIndex === -1) {
      if (unsubscribe) unsubscribe();
      return { success: false, reason: 'full' };
    }

    const result = await new Promise<{ committed: boolean; value: boolean }>((resolve, reject) => {
      runTransaction(matchRef, (data: MatchData | null) => {
        if (!data) return;
        if (data.status !== 'waiting') return;

        // Double-check: is this uid already in the match?
        const seatIdx = data.players.findIndex(p => p.uid === guestUid);
        if (seatIdx !== -1) return; // Already a member

        const emptyIdx = data.players.findIndex(p => p.uid === '');
        if (emptyIdx === -1) return;

        data.players[emptyIdx].uid = guestUid;
        data.players[emptyIdx].name = guestName;

        const messages = [...(data.messages || [])];
        messages.push(`${guestName} si è unito alla partita.`);

        const allFilled = data.players.every(p => p.uid !== '');
        if (data.numPlayers === 2 && allFilled) {
          data.status = 'playing';
          messages.push('La partita inizia!');
          data.currentPlayerIndex = 0;
        }

        data.messages = messages;

        return sanitizeForFirebase(data);
      }).then(({ committed, snapshot }) => {
        if (!committed) {
          resolve({ committed: false, value: false });
          return;
        }
        const val = snapshot.val() as MatchData;
        const joinedIndex = val?.players.findIndex(p => p.uid === guestUid);
        resolve({ committed: true, value: joinedIndex !== -1 });
      }).catch(reject);
    });

    if (unsubscribe) unsubscribe();

    if (result.value) {
      trackMyMatch(code);
    }
    return { success: result.value };
  } catch (err) {
    console.error('[matchmaking] joinMatch failed:', err);
    throw new Error(`[matchmaking] Impossibile unirsi alla partita: ${err instanceof Error ? err.message : err}`);
  }
}

/**
 * Update only the caller's connection flag.
 *
 * Presence is deliberately a seat-scoped write instead of a transaction over the
 * whole match. During an active game the match-level Firebase rule only grants
 * full-state writes to the current player; using that path for presence made a
 * reconnect depend on whose turn it happened to be. The dedicated child rule for
 * `players/$idx/disconnected` verifies that the authenticated UID owns the seat.
 */
async function setPlayerDisconnectedState(code: string, uid: string, disconnected: boolean): Promise<boolean> {
  const matchRef = ref(db, `matches/${code}`);
  const snapshot = await get(matchRef);
  if (!snapshot.exists()) return false;

  const data = snapshot.val() as MatchData;
  const seatIdx = data.players?.findIndex(player => player.uid === uid) ?? -1;
  if (seatIdx === -1) return false;

  const disconnectedRef = ref(db, `matches/${code}/players/${seatIdx}/disconnected`);
  await set(disconnectedRef, disconnected);
  return true;
}

/** Rejoin an existing match after disconnect.
 * Finds the player's persistent seat by uid and clears only its presence flag.
 * This is idempotent and works regardless of whose turn it is.
 */
export async function rejoinMatch(code: string, uid: string, guestName?: string): Promise<boolean> {
  try {
    // Keep the original seat identity/name intact. Updating the whole player or
    // match here would reintroduce the turn-dependent permission problem.
    void guestName;
    const rejoined = await setPlayerDisconnectedState(code, uid, false);
    if (rejoined) {
      trackMyMatch(code);
    }
    return rejoined;
  } catch (err) {
    console.error('[matchmaking] rejoinMatch failed:', err);
    throw new Error(`[matchmaking] Impossibile riconnettersi alla partita: ${err instanceof Error ? err.message : err}`);
  }
}

/** Swap/claim seat in lobby */
export async function swapSeat(code: string, uid: string, targetIndex: number): Promise<boolean> {
  const matchRef = ref(db, `matches/${code}`);
  try {
    const result = await new Promise<{ committed: boolean; value: boolean }>((resolve, reject) => {
      runTransaction(matchRef, (data: MatchData | null) => {
        if (!data) return;
        if (data.status !== 'waiting') return;
        if (targetIndex < 0 || targetIndex >= data.numPlayers) return;

        const currentSeatIndex = data.players.findIndex(p => p.uid === uid);
        if (currentSeatIndex === -1) return;

        const tempUid = data.players[targetIndex].uid;
        const tempName = data.players[targetIndex].name;

        data.players[targetIndex].uid = uid;
        data.players[targetIndex].name = data.players[currentSeatIndex].name;

        data.players[currentSeatIndex].uid = tempUid;
        data.players[currentSeatIndex].name = tempName;

        return sanitizeForFirebase(data);
      }).then(({ committed, snapshot }) => {
        if (!committed) {
          resolve({ committed: false, value: false });
          return;
        }
        const val = snapshot.val() as MatchData;
        resolve({ committed: true, value: val?.players[targetIndex]?.uid === uid });
      }).catch(reject);
    });
    return result.value;
  } catch (err) {
    console.error('[matchmaking] swapSeat failed:', err);
    return false;
  }
}

/** Start match manually (for 3 & 4 players) */
export async function startMatch(code: string, uid: string): Promise<void> {
  const matchRef = ref(db, `matches/${code}`);
  try {
    await runTransaction(matchRef, (data: MatchData | null) => {
      if (!data) return;
      if (data.status !== 'waiting') return;
      if (data.hostUid !== uid) {
        console.warn('Solo l\'host può avviare la partita');
        return;
      }

      const allFilled = data.players.every(p => p.uid !== '');
      if (!allFilled) {
        console.warn('Tutti i posti devono essere occupati per iniziare');
        return;
      }

      const deck = shuffle(createDeck());
      const table = deck.splice(0, 4);

      for (let i = 0; i < data.numPlayers; i++) {
        data.players[i].hand = deck.splice(0, 3);
        data.players[i].captured = [];
        data.players[i].totalScore = 0;
        data.players[i].hasDeclaredLowHand = false;
        data.players[i].hasDeclaredThreeAces = false;
        data.players[i].lowHandCount = 0;
        data.players[i].threeAcesCount = 0;
      }

      data.deck = deck;
      data.table = table;
      data.status = 'playing';
      data.currentPlayerIndex = 0;
      data.roundStartPlayer = 0;
      data.lastPlayedCard = null;
      data.lastToCapture = null;
      data.messages = ['La partita inizia!', `${data.players[0].name} inizia.`];
      data.scope = Array(data.numPlayers).fill(0);
      data.revealedHands = Array(data.numPlayers).fill([]).map(() => []);

      return sanitizeForFirebase(data);
    });
  } catch (err) {
    console.error('[matchmaking] startMatch failed:', err);
    throw new Error(`[matchmaking] Impossibile avviare la partita: ${err instanceof Error ? err.message : err}`);
  }
}

/** Listen to match changes */
export function listenMatch(
  code: string,
  callback: (match: MatchData | null) => void,
  onError?: (error: Error) => void
): () => void {
  const matchRef = ref(db, `matches/${code}`);
  const handler = (snapshot: any) => {
    if (snapshot.exists()) {
      callback(snapshot.val() as MatchData);
    } else {
      callback(null);
    }
  };
  const errorHandler = (error: Error) => {
    console.error('[matchmaking] listenMatch error:', error);
    if (onError) onError(error);
  };
  onValue(matchRef, handler, errorHandler);
  return () => off(matchRef, 'value', handler);
}

/** Play a card and handle capture/no-capture */
export async function playCard(
  code: string,
  playerIndex: number,
  card: Card,
  capturedCards: Card[] | null,
  uid: string
): Promise<void> {
  const matchRef = ref(db, `matches/${code}`);

  await new Promise<void>((resolve, reject) => {
    runTransaction(matchRef, (data: MatchData | null) => {
      if (!data) return;

      if (data.currentPlayerIndex !== playerIndex) {
        console.warn(`Turn mismatch: currentPlayer=${data.currentPlayerIndex}, actor=${playerIndex}`);
        return;
      }

      const playerUid = data.players[playerIndex]?.uid;
      if (playerUid !== uid) {
        console.warn(`UID mismatch: playerUid=${playerUid}, actor=${uid}`);
        return;
      }

      if (data.status !== 'playing') return;

      let currentHand = data.players[playerIndex].hand || [];
      let newTable = data.table || [];
      let newCaptured = [...(data.players[playerIndex].captured || [])];
      let messages = [...(data.messages || [])];
      let lastToCapture: number | null = data.lastToCapture;
      let lastPlayedCard: { card: Card; playerIndex: number } | null = { card, playerIndex };

      if (capturedCards !== null) {
        currentHand = currentHand.filter((c: Card) => c.id !== card.id);
        newTable = newTable.filter((c: Card) => !capturedCards.some((cc: Card) => cc.id === c.id));
        newCaptured = [...newCaptured, card, ...capturedCards];
        lastToCapture = playerIndex;

        if (capturedCards.length > 0) {
          if (isScopaMove(card, data.table || [], capturedCards)) {
            messages = [`SCOPA!`, ...messages.slice(0, 4)];
            if (!data.scope) data.scope = Array(data.numPlayers).fill(0);
            data.scope[playerIndex] = (data.scope[playerIndex] || 0) + 1;
          } else {
            messages = [`${data.players[playerIndex].name} cattura ${capturedCards.length} carta(e)`, ...messages.slice(0, 4)];
          }
        } else {
          messages = [`${data.players[playerIndex].name} prende l'asso`, ...messages.slice(0, 4)];
        }
      } else {
        currentHand = currentHand.filter((c: Card) => c.id !== card.id);
        newTable = [...newTable, card];
        messages = [`${data.players[playerIndex].name} gioca ${card.value}`, ...messages.slice(0, 4)];
      }

      const nextPlayerIndex = (playerIndex + data.numPlayers - 1) % data.numPlayers;
      let deckRemaining = data.deck || [];

      data.players[playerIndex].hand = currentHand;
      data.table = newTable;
      data.lastPlayedCard = lastPlayedCard;
      data.lastToCapture = lastToCapture !== undefined && lastToCapture !== null ? lastToCapture : null;
      data.messages = messages;
      data.currentPlayerIndex = nextPlayerIndex;

      if (capturedCards !== null) {
        data.players[playerIndex].captured = newCaptured;
      }

      // Deal logic
      const allHandsEmpty = data.players.every(p => (p.hand || []).length === 0);
      if (allHandsEmpty) {
        const dealSize = 3 * data.numPlayers;
        if (deckRemaining.length >= dealSize) {
          for (let i = 0; i < data.numPlayers; i++) {
            data.players[i].hand = deckRemaining.splice(0, 3);
            data.players[i].hasDeclaredLowHand = false;
            data.players[i].hasDeclaredThreeAces = false;
          }
          data.deck = deckRemaining;
          data.revealedHands = Array(data.numPlayers).fill([]).map(() => []);
        } else {
          // Defensive: any remaining deck + table cards go to the last capturer
          // (or current player if no captures this round). Covers the case
          // where 0 < deckRemaining.length < dealSize — previously this branch
          // was silently dropped, hanging the game.
          const tail: Card[] = [...newTable, ...deckRemaining];
          if (lastToCapture !== null) {
            data.players[lastToCapture].captured = [...(data.players[lastToCapture].captured || []), ...tail];
          } else {
            data.players[playerIndex].captured = [...(data.players[playerIndex].captured || []), ...tail];
          }
          data.table = [];
          data.deck = [];

          const { totalScores } = calculateOnlineRoundScores(data);
          for (let i = 0; i < data.numPlayers; i++) {
            data.players[i].totalScore = totalScores[i];
          }

          const maxScore = Math.max(...totalScores);
          const limit = data.targetScore ?? GAME_CONSTANTS.TARGET_SCORE;
          if (maxScore >= limit) {
            data.status = 'finished';
          } else {
            data.status = 'roundEnd';
            data.roundEndedAt = Date.now();
            for (let i = 0; i < data.numPlayers; i++) {
              data.players[i].readyForNextRound = false;
            }
            data.messages = [`Round terminato!`, `Prossimo round in arrivo...`];
          }
        }
      }

      return sanitizeForFirebase(data);
    }).then(({ committed }) => {
      if (!committed) {
        console.warn('playCard transaction not committed');
      }
      resolve();
    }).catch(reject);
  });

  // Previously this was done via a setTimeout that read match state and wrote
  // lastPlayedCard: null. That created a race window: another player's
  // runTransaction could fire before the timeout executed, reading a stale
  // lastPlayedCard from the previous turn. The clear now happens inside the
  // transaction above (data.lastPlayedCard = null before return), so the clear
  // commits atomically with the rest of the state.
}

/** Start next round after roundEnd */
export async function startNextRound(code: string, uid: string): Promise<void> {
  const matchRef = ref(db, `matches/${code}`);
  try {
    await runTransaction(matchRef, (data: MatchData | null) => {
      if (!data) return;
      const mySeatIdx = data.players.findIndex(p => p.uid === uid);
      if (mySeatIdx === -1) return;
      if (data.status !== 'roundEnd') return;

      // Mark this player as ready
      data.players[mySeatIdx].readyForNextRound = true;

      // Check if all players are ready, or if 30s has passed since roundEndedAt
      const allReady = data.players.every(p => p.readyForNextRound === true);
      const isTimeout = data.roundEndedAt ? (Date.now() - data.roundEndedAt >= 30000) : false;

      if (allReady || isTimeout) {
        const nextStarter = (data.roundStartPlayer + data.numPlayers - 1) % data.numPlayers;
        const starterName = data.players[nextStarter].name || 'Avversario';
        const newDeck = shuffle(createDeck());
        const newTable = newDeck.splice(0, 4);

        for (let i = 0; i < data.numPlayers; i++) {
          data.players[i].hand = newDeck.splice(0, 3);
          data.players[i].captured = [];
          data.players[i].hasDeclaredLowHand = false;
          data.players[i].hasDeclaredThreeAces = false;
          data.players[i].lowHandCount = 0;
          data.players[i].threeAcesCount = 0;
          data.players[i].readyForNextRound = false; // Reset readiness
        }

        data.status = 'playing';
        data.deck = newDeck;
        data.table = newTable;
        data.scope = Array(data.numPlayers).fill(0);
        data.revealedHands = Array(data.numPlayers).fill([]).map(() => []);
        data.currentPlayerIndex = nextStarter;
        data.roundStartPlayer = nextStarter;
        data.lastToCapture = null;
        data.lastPlayedCard = null;
        data.messages = [`Nuovo round!`, `${starterName} inizia.`];
        data.roundEndedAt = undefined;
      } else {
        const readyCount = data.players.filter(p => p.readyForNextRound).length;
        data.messages = [`In attesa dei giocatori... (${readyCount}/${data.numPlayers} pronti)`];
      }

      return sanitizeForFirebase(data);
    });
  } catch (err) {
    console.error('[matchmaking] startNextRound failed:', err);
    throw new Error(`[matchmaking] Impossibile avviare il prossimo round: ${err instanceof Error ? err.message : err}`);
  }
}

const PENDING_MATCH_KEY = 'sbarazzo_pending_match';

export interface PendingMatch {
  matchId: string;
  uid: string;
  isHost: boolean;
  myName: string;
  timestamp: number;
}

export function savePendingMatch(data: PendingMatch): void {
  try {
    localStorage.setItem(PENDING_MATCH_KEY, JSON.stringify(data));
  } catch (e) {
    console.warn('Failed to save pending match', e);
  }
}

export function getPendingMatch(): PendingMatch | null {
  try {
    const raw = localStorage.getItem(PENDING_MATCH_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as PendingMatch;
    if (Date.now() - data.timestamp > GAME_CONSTANTS.MATCH_TTL_24H) {
      localStorage.removeItem(PENDING_MATCH_KEY);
      return null;
    }
    return data;
  } catch (e) {
    return null;
  }
}

export function clearPendingMatch(): void {
  try {
    localStorage.removeItem(PENDING_MATCH_KEY);
  } catch (e) {}
}

/** Calculate round scores for online players */
export function calculateOnlineRoundScores(
  match: MatchData
): { totalScores: number[]; results: any[] } {
  const players: Player[] = match.players.map((p, idx) => ({
    id: idx,
    name: p.name || `Giocatore ${idx + 1}`,
    hand: p.hand || [],
    captured: p.captured || [],
    currentRoundScore: 0,
    totalScore: p.totalScore || 0,
    scope: match.scope ? (match.scope[idx] || 0) : 0,
    hasDeclaredLowHand: p.hasDeclaredLowHand || false,
    hasDeclaredThreeAces: p.hasDeclaredThreeAces || false,
    lowHandCount: p.lowHandCount || 0,
    threeAcesCount: p.threeAcesCount || 0,
  }));

  const roundResults = calculateRoundScores(players, match.numPlayers);

  const totalScores = match.players.map((p, idx) => {
    const rawTotal = (p.totalScore || 0) + roundResults.totalScores[idx];
    return rawTotal === -11 ? 0 : rawTotal;
  });

  return {
    totalScores,
    results: roundResults.results,
  };
}

/** Declare low hand (Sbarazzo) */
export async function declareLowHand(
  code: string,
  playerIndex: number,
  uid: string
): Promise<void> {
  const matchRef = ref(db, `matches/${code}`);

  await new Promise<void>((resolve, reject) => {
    runTransaction(matchRef, (data: MatchData | null) => {
      if (!data) return;
      if (data.status !== 'playing') return;
      if (data.currentPlayerIndex !== playerIndex) return;

      const playerUid = data.players[playerIndex]?.uid;
      if (playerUid !== uid) return;

      const player = data.players[playerIndex];
      const hand = player.hand || [];
      const sum = hand.reduce((s, c) => s + c.value, 0);

      if (hand.length === 3 && sum <= GAME_CONSTANTS.SBARAZZO_DECLARE_THRESHOLD && !player.hasDeclaredLowHand) {
        const playerName = player.name;
        const messages = [`${playerName} dichiara sbarazzo (somma ${sum})! +3 punti.`, ...(data.messages || []).slice(0, 4)];

        data.players[playerIndex].hasDeclaredLowHand = true;
        data.players[playerIndex].lowHandCount = (data.players[playerIndex].lowHandCount || 0) + 1;
        if (!data.revealedHands) {
          data.revealedHands = Array(data.numPlayers).fill([]).map(() => []);
        }
        data.revealedHands[playerIndex] = hand.map(c => c.id);
        data.messages = messages;

        return sanitizeForFirebase(data);
      }
      return;
    }).then(({ committed }) => {
      resolve();
    }).catch(reject);
  });
}

/** Declare three aces */
export async function declareThreeAces(
  code: string,
  playerIndex: number,
  uid: string
): Promise<void> {
  const matchRef = ref(db, `matches/${code}`);

  await new Promise<void>((resolve, reject) => {
    runTransaction(matchRef, (data: MatchData | null) => {
      if (!data) return;
      if (data.status !== 'playing') return;
      if (data.currentPlayerIndex !== playerIndex) return;

      const playerUid = data.players[playerIndex]?.uid;
      if (playerUid !== uid) return;

      const player = data.players[playerIndex];
      const hand = player.hand || [];
      const aces = hand.filter(c => c.value === 1);

      if (aces.length === 3 && !player.hasDeclaredThreeAces) {
        const playerName = player.name;
        const messages = [`${playerName} dichiara tre assi! +6 punti.`, ...(data.messages || []).slice(0, 4)];

        data.players[playerIndex].hasDeclaredThreeAces = true;
        data.players[playerIndex].threeAcesCount = (data.players[playerIndex].threeAcesCount || 0) + 1;
        data.messages = messages;

        return sanitizeForFirebase(data);
      }
      return;
    }).then(({ committed }) => {
      resolve();
    }).catch(reject);
  });
}

const MY_MATCHES_KEY = 'sbarazzo_my_matches';

function trackMyMatch(code: string): void {
  try {
    const raw = localStorage.getItem(MY_MATCHES_KEY);
    const codes: string[] = raw ? JSON.parse(raw) : [];
    if (!codes.includes(code)) {
      codes.push(code);
      localStorage.setItem(MY_MATCHES_KEY, JSON.stringify(codes));
    }
  } catch (e) {
    console.warn('Failed to track match code', e);
  }
}

export async function deleteMatch(code: string): Promise<void> {
  const matchRef = ref(db, `matches/${code}`);
  await set(matchRef, null);
}

export async function cleanupOldMatches(uid: string): Promise<void> {
  try {
    const raw = localStorage.getItem(MY_MATCHES_KEY);
    if (!raw) return;
    const codes: string[] = JSON.parse(raw);
    if (codes.length === 0) return;

    const remainingCodes: string[] = [];
    const now = Date.now();
    const TTL = GAME_CONSTANTS.MATCH_TTL_48H;

    for (const code of codes) {
      try {
        const matchRef = ref(db, `matches/${code}`);
        const snapshot = await get(matchRef);
        if (!snapshot.exists()) {
          continue;
        }

        const match = snapshot.val() as MatchData;
        const isMe = match.players?.some(p => p.uid === uid);
        if (!isMe) {
          continue;
        }

        const age = now - (match.createdAt ?? now);
        const shouldDelete = 
          match.status === 'finished' ? age > TTL :
          match.status === 'waiting' && match.hostUid === uid ? age > TTL : false;

        if (shouldDelete) {
          await deleteMatch(code);
        } else {
          remainingCodes.push(code);
        }
      } catch (err) {
        console.warn(`Failed to clean up match ${code}:`, err);
        remainingCodes.push(code);
      }
    }

    localStorage.setItem(MY_MATCHES_KEY, JSON.stringify(remainingCodes));
  } catch (e) {
    console.warn('cleanupOldMatches failed:', e);
  }
}

/** Mark a player as disconnected without modifying the match state. */
export async function markDisconnected(code: string, uid: string): Promise<void> {
  try {
    await setPlayerDisconnectedState(code, uid, true);
  } catch (err) {
    console.warn('[matchmaking] markDisconnected failed (non-critical):', err);
  }
}

/** Leave a match (explicit user action — cancel/leave button).
 *
 * Philosophy: NEVER delete a match. Just mark the player as gone so the match
 * can be cleaned up later by TTL logic or explicitly.
 */
export async function leaveMatch(code: string, uid: string): Promise<void> {
  const matchRef = ref(db, `matches/${code}`);
  try {
    await runTransaction(matchRef, (data: MatchData | null) => {
      if (!data) return;
      const seatIdx = data.players.findIndex(p => p.uid === uid);
      if (seatIdx === -1) return;

      const playerName = data.players[seatIdx].name || 'Un giocatore';
      const isHost = data.hostUid === uid;

      // Always: mark seat as empty, mark disconnected
      data.players[seatIdx].uid = '';
      data.players[seatIdx].name = '';
      data.players[seatIdx].disconnected = true;

      // If host left waiting lobby, release host ownership so another player can
      // take over without stale hostUid blocking seat assignment.
      if (isHost && data.status === 'waiting') {
        data.hostUid = '';
      }

      data.messages = [
        `${playerName} ha lasciato la partita.`,
        ...(data.messages || []).slice(0, 4),
      ];

      return sanitizeForFirebase(data);
    });
  } catch (err) {
    console.warn('[matchmaking] leaveMatch failed:', err);
  }
}

/** Send a real-time emote during a match.
 * Uses a transaction to avoid race conditions: if two emotes race to Firebase,
 * only the one with the newer timestamp wins. */
export async function sendEmote(
  matchId: string,
  playerIndex: number,
  emote: 'angry' | 'cool' | 'shocked' | 'handshake' | 'lol'
): Promise<void> {
  const emoteRef = ref(db, `matches/${matchId}/lastEmote`);
  const timestamp = Date.now();
  try {
    await runTransaction(emoteRef, (current: { playerIndex: number; emote: string; timestamp: number } | null) => {
      // Only write if no existing emote or incoming timestamp is strictly newer.
      // This prevents out-of-order delivery: an older emote arriving late is dropped.
      if (!current || timestamp > current.timestamp) {
        return { playerIndex, emote, timestamp };
      }
      // Abort transaction — keep existing (newer) value
      return;
    });
  } catch (err) {
    console.error('[matchmaking] sendEmote error:', err);
  }
}

/** Send a chat message during a match.
 * Writes to lastMessage so all players receive it in real-time.
 * No persistence after match ends. */
export async function sendMessage(
  matchId: string,
  playerIndex: number,
  text: string
): Promise<void> {
  const msgRef = ref(db, `matches/${matchId}/lastMessage`);
  const timestamp = Date.now();
  try {
    await runTransaction(msgRef, (current: { playerIndex: number; text: string; timestamp: number } | null) => {
      if (!current || timestamp > current.timestamp) {
        return { playerIndex, text, timestamp };
      }
      return;
    });
  } catch (err) {
    console.error('[matchmaking] sendMessage error:', err);
  }
}

export interface ChallengeData {
  challengeId: string;
  fromUid: string;
  fromName: string;
  matchCode: string;
  createdAt: number;
}

/** Send a friend challenge: challenger creates the match, then writes a challenge entry under the friend's user node. */
export async function challengeFriend(
  myUid: string,
  myName: string,
  friendUid: string
): Promise<string> {
  // Step 1: challenger creates the match (becomes host)
  const code = await reserveMatchCode(myUid);
  const matchRef = ref(db, `matches/${code}`);

  const deck = shuffle(createDeck());
  const table = deck.splice(0, 4);

  const players: MatchPlayer[] = [
    { uid: myUid, name: myName, hand: deck.splice(0, 3), captured: [], totalScore: 0 },
    { uid: '', name: '', hand: deck.splice(0, 3), captured: [], totalScore: 0 },
  ];

  const matchData: MatchData = {
    matchId: code,
    hostUid: myUid,
    numPlayers: 2,
    status: 'waiting',
    deck,
    table,
    players,
    targetScore: GAME_CONSTANTS.TARGET_SCORE,
    currentPlayerIndex: 0,
    roundStartPlayer: 0,
    lastPlayedCard: null,
    lastToCapture: null,
    messages: [`Sfida da ${myName} — in attesa...`],
    scope: [0, 0],
    revealedHands: [[], []],
    createdAt: Date.now(),
  };

  await set(matchRef, matchData);
  trackMyMatch(code);

  // Step 2: write challenge entry for the friend with the matchCode already present
  const challengeId = `ch_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const challengeRef = ref(db, `challenges/${friendUid}/${challengeId}`);
  const challenge: ChallengeData = {
    challengeId,
    fromUid: myUid,
    fromName: myName,
    matchCode: code,
    createdAt: Date.now(),
  };
  await set(challengeRef, challenge);
  return challengeId;
}

/** Accept a friend challenge: the challenged player creates the match and records the matchCode on the challenge node. */
export async function acceptChallenge(
  challengeId: string,
  challengerUid: string,
  challengerName: string,
  challengedUid: string,
  challengedName: string,
  numPlayers: number = 2,
  targetScore: number = 16
): Promise<string> {
  // Create match as the challenged player (who accepts the challenge)
  const code = await reserveMatchCode(challengedUid);
  const matchRef = ref(db, `matches/${code}`);

  const deck = shuffle(createDeck());
  const table = deck.splice(0, 4);

  const players: MatchPlayer[] = [
    { uid: challengedUid, name: challengedName, hand: deck.splice(0, 3), captured: [], totalScore: 0 },
    { uid: challengerUid, name: challengerName, hand: deck.splice(0, 3), captured: [], totalScore: 0 },
  ];

  const matchData: MatchData = {
    matchId: code,
    hostUid: challengedUid,
    numPlayers,
    status: 'playing',
    deck,
    table,
    players,
    targetScore,
    currentPlayerIndex: 0,
    roundStartPlayer: 0,
    lastPlayedCard: null,
    lastToCapture: null,
    messages: [`Sfida accettata! ${challengedName} vs ${challengerName}`, 'La partita inizia!'],
    scope: Array(numPlayers).fill(0),
    revealedHands: Array(numPlayers).fill([]).map(() => []),
    createdAt: Date.now(),
  };

  await set(matchRef, matchData);

  // Update the challenge node with the matchCode so the challenger can find it
  const challengeRef = ref(db, `challenges/${challengerUid}/${challengeId}`);
  await update(challengeRef, { matchCode: code });

  trackMyMatch(code);
  return code;
}

/** Listen for incoming challenges for a given uid. */
export function listenChallenges(
  uid: string,
  callback: (challenges: ChallengeData[]) => void
): () => void {
  const challengesRef = ref(db, `challenges/${uid}`);
  const handler = (snapshot: any) => {
    const val = snapshot.val();
    if (!val) {
      callback([]);
      return;
    }
    const list: ChallengeData[] = Object.values(val);
    callback(list);
  };
  onValue(challengesRef, handler);
  return () => off(challengesRef, 'value', handler);
}

/** Remove a challenge entry. */
export async function removeChallenge(uid: string, challengeId: string): Promise<void> {
  const challengeRef = ref(db, `challenges/${uid}/${challengeId}`);
  await set(challengeRef, null);
}

/** Transfer host to another player (called when host disconnects/leaves waiting). */
export async function transferHost(code: string, newHostUid: string): Promise<void> {
  const matchRef = ref(db, `matches/${code}`);
  try {
    await runTransaction(matchRef, (data: MatchData | null) => {
      if (!data) return;
      const newHostSeatIdx = data.players.findIndex(p => p.uid === newHostUid);
      if (newHostSeatIdx === -1) return;

      data.hostUid = newHostUid;
      data.messages = [
        `${data.players[newHostSeatIdx].name || 'Un giocatore'} è diventato l'host.`,
        ...(data.messages || []).slice(0, 4),
      ];

      return sanitizeForFirebase(data);
    });
  } catch (err) {
    console.warn('[matchmaking] transferHost failed:', err);
  }
}
